import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDownIcon } from 'lucide-react';
import { PrimaryButton } from './PrimaryButton';
import { formatMicrograms } from '../../utils/rekovelleDose';
type RevealScreenProps = {
  amh: number;
  weight: number;
  dose: number;
  onNext: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function RevealScreen({
  amh,
  weight,
  dose,
  onNext
}: RevealScreenProps) {
  const reduced = useReducedMotion() ?? false;
  const steps = [{
    title: 'AMH + body weight',
    caption: `${amh} pmol/L · ${weight} kg`
  }, {
    title: 'Defined individualized first-cycle starting dose',
    caption: 'Set before stimulation begins',
    dose: true
  }, {
    title: 'Designed around an appropriate ovarian response',
    caption: 'Not maximum stimulation'
  }];
  return <div className="flex h-full flex-col" data-id="element-252" data-mp="1hhzedr660zfy">
      <h2 className="text-[32px] font-semibold leading-[1.04] tracking-[-0.035em] text-ink" data-id="element-253" data-mp="1cqhe75byv2w1">
        <span className="text-accent" data-id="element-254" data-mp="1coo4fh6242jt">REKOVELLE</span> starts with the patient.
      </h2>

      <ol className="my-auto flex flex-col items-stretch py-5" data-id="element-255" data-mp="3dnr1ciin1q1n">
        {steps.map((s, i) => <motion.li key={s.title} initial={reduced ? false : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.24,
        ease: EASE,
        delay: reduced ? 0 : 0.1 + i * 0.16
      }} className="flex flex-col items-center" data-id="element-256" data-mp="3hpc95pgbfe4z" data-mpi={i}>
            {i > 0 && <ArrowDownIcon size={18} strokeWidth={1.75} className="my-2 text-graphite/60" aria-hidden="true" data-id="element-257" data-mp="2ywql3rbngruy" />}
            {s.dose ? <div className="w-full rounded-2xl bg-accent px-5 py-4 text-center text-white" data-id="element-258" data-mp="2ky0la3xg4e2w">
                <p className="text-[15px] font-semibold leading-tight tracking-[-0.01em]" data-id="element-259" data-mp="2px3gyxrjd7y7">{s.title}</p>
                <p className="mt-2 text-[34px] font-semibold leading-none tabular-nums tracking-[-0.03em]" data-id="element-260" data-mp="j6nl1s6faud6">{formatMicrograms(dose)}</p>
                <p className="mt-1.5 font-mono text-[9.5px] tracking-[0.12em] text-white/75" data-id="element-261" data-mp="o2me3t2p50jx">PER DAY · FOR THIS PATIENT</p>
              </div> : <div className="w-full rounded-2xl border border-ink/10 bg-white px-5 py-3.5 text-center text-ink" data-id="element-262" data-mp="hndsd3iywkh1">
                <p className="text-[16px] font-semibold leading-tight tracking-[-0.015em]" data-id="element-263" data-mp="cq590zm32ae7">{s.title}</p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.12em] text-graphite" data-id="element-264" data-mp="7rtx8jc89ptc">{s.caption.toUpperCase()}</p>
              </div>}
          </motion.li>)}
      </ol>

      <p className="text-[15px] leading-snug text-graphite" data-id="element-265" data-mp="10g9ab0bsty21">
        The starting dose is determined from patient characteristics before stimulation begins.
      </p>
      <p className="mt-1.5 text-[10px] leading-snug text-graphite/70" data-id="element-266" data-mp="qpn39ui5efuy">
        Illustrative calculation following the labelled first-cycle algorithm. Verify against the approved SmPC.
      </p>

      <div className="mt-5" data-id="element-267" data-mp="1jdai0swqmc9o">
        <PrimaryButton onClick={onNext} data-id="element-268" data-mp="1jd0q2zs4tv1c">CONTINUE</PrimaryButton>
      </div>
    </div>;
}